import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Globe,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { INDIAN_STATES, stateFromGstin } from "@/lib/indianStates";
import type { AccountRecord, MasterLookupRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { BuyerTermsFields } from "@/components/accounts/BuyerTermsFields";
import { EMPTY_BUYER_TERMS, buyerTermsFrom, buyerTermsPayload, type BuyerTermsForm } from "@/lib/buyerTerms";
import { FilterBar } from "@/components/filters/FilterBar";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, valueOptionsFromFacets, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  Factory as FilterIndustryIcon,
  MapPin as FilterStateIcon,
  UserCircle2 as FilterOwnerIcon,
  ShieldCheck as FilterValidityIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const ACCOUNT_FILTER_DEFAULTS = { owner: "all", industry: "all", state: "all", created: "any" };

export function AccountsPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  useEffect(() => setSearch(searchParams.get("search") || ""), [searchParams]);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<AccountRecord | null>(null);
  const [deleteAccount, setDeleteAccount] = useState<AccountRecord | null>(null);

  // Form State
  const [form, setForm] = useState({
    name: "",
    industry: "",
    website: "",
    email: "",
    phone: "",
    billing_address: "",
    shipping_address: "",
    state: "",
    gstin: "",
    notes: "",
  });

  const [terms, setTerms] = useState<BuyerTermsForm>(EMPTY_BUYER_TERMS);
  const [industries, setIndustries] = useState<MasterLookupRecord[]>([]);

  const { success, error } = useToast();
  const { user } = useAuth();
  const accountFilters = usePersistentFilters("accounts", ACCOUNT_FILTER_DEFAULTS);
  const [accountOptions, setAccountOptions] = useState<{
    total: number;
    owners: OwnerFacet[];
    industries: { value: string; count: number }[];
    states: { value: string; count: number }[];
  }>({ total: 0, owners: [], industries: [], states: [] });

  const list = usePagedList<AccountRecord>({
    deps: [accountFilters.values, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = accountFilters.values;
      const query = toQuery({
        search: search.trim(),
        owner: f.owner,
        industry: f.industry,
        state: f.state,
        ...dateRangeParams(f.created, "created"),
        page,
        page_size: 30,
      });
      try {
        const [accRes, indRes, optRes] = await Promise.all([
          api.get(`/api/Account/List${query}`),
          api.get("/api/MasterLookup/ByCategory/INDUSTRY"),
          api.get("/api/Account/FilterOptions").catch(() => null),
        ]);
        if (optRes) setAccountOptions(unwrapObject(optRes));
        setIndustries(unwrapArray<MasterLookupRecord>(indRes));
        return unwrapPage<AccountRecord>(accRes);
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load accounts");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const accounts = list.rows;
  const loading = list.loading;
  const loadAccounts = list.reload;

  const openAdd = () => {
    setForm({
      name: "",
      industry: "",
      website: "",
      email: "",
      phone: "",
      billing_address: "",
      shipping_address: "",
      state: "",
      gstin: "",
      notes: "",
    });
    setTerms(EMPTY_BUYER_TERMS);
    setAddOpen(true);
  };

  const openEdit = (a: AccountRecord) => {
    setEditAccount(a);
    setForm({
      name: a.name || "",
      industry: a.industry || "",
      website: a.website || "",
      email: a.email || "",
      phone: a.phone || "",
      billing_address: a.billing_address || "",
      shipping_address: a.shipping_address || "",
      state: a.state || "",
      gstin: a.gstin || "",
      notes: a.notes || "",
    });
    setTerms(buyerTermsFrom(a));
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await api.post("/api/Account/Insert", { ...form, ...buyerTermsPayload(terms) });
      success("Account Created", "Company account successfully added");
      setAddOpen(false);
      loadAccounts();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create account");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAccount) return;
    setActionLoading(true);
    try {
      await api.put(`/api/Account/Update/${editAccount.id}`, { ...form, ...buyerTermsPayload(terms) });
      success("Account Updated", "Company details saved");
      setEditAccount(null);
      loadAccounts();
    } catch (err: any) {
      error("Update Failed", err.message || "Could not update account");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteAccount) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Account/Delete/${deleteAccount.id}`);
      success("Account Deleted", "Company account removed");
      setDeleteAccount(null);
      loadAccounts();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete account");
    } finally {
      setActionLoading(false);
    }
  };

  // The API already applied every filter
  const filtered = accounts;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Building2 className="size-6 text-primary" />
            Accounts & Companies
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage organization clients, procurement departments, and enterprise relationships
          </p>
        </div>

        <Button onClick={openAdd} className="gap-1.5 bg-primary hover:bg-primary/90">
          <Plus className="size-4" />
          Add Account
        </Button>
      </div>

      {/* Filters */}
      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search accounts by name, buyer type, email, phone..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(accountOptions.owners, accountOptions.total, user?.Id), defaultValue: "all" },
          { name: "industry", label: "Buyer Type", icon: FilterIndustryIcon, type: "select", options: valueOptionsFromFacets(accountOptions.industries, "All buyer types", accountOptions.total), defaultValue: "all" },
          { name: "state", label: "State", icon: FilterStateIcon, type: "select", options: valueOptionsFromFacets(accountOptions.states, "All states", accountOptions.total), defaultValue: "all" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={accountFilters.values}
        onChange={(name, value) => accountFilters.set(name as keyof typeof ACCOUNT_FILTER_DEFAULTS, value)}
        onReset={accountFilters.reset}
        shown={list.total}
        total={list.total}
        noun="accounts"
      />

      {/* Table */}
      {loading && accounts.length === 0 ? (
        <TableSkeleton />
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 text-xs">
                <TableHead>Company Name</TableHead>
                <TableHead>Buyer Type</TableHead>
                <TableHead>Contact Info</TableHead>
                <TableHead>Account Owner</TableHead>
                <TableHead>Created On</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground">
                    No accounts found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((acc) => (
                  <TableRow key={acc.id} className="text-xs">
                    <TableCell className="font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <div className="size-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                          {acc.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <Link
                            to={`/accounts/${acc.id}`}
                            className="font-semibold text-foreground hover:text-primary hover:underline"
                          >
                            {acc.name}
                          </Link>
                          {acc.website && (
                            <a
                              href={acc.website.startsWith("http") ? acc.website : `https://${acc.website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1"
                            >
                              <Globe className="size-3" />
                              {acc.website.replace(/^https?:\/\//, "")}
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{acc.industry || "—"}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-[11px]">
                        {acc.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="size-3 text-muted-foreground/70" />
                            {acc.email}
                          </div>
                        )}
                        {acc.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="size-3 text-muted-foreground/70" />
                            {acc.phone}
                          </div>
                        )}
                        {!acc.email && !acc.phone && "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      {acc.owner ? (
                        <div className="flex items-center gap-1.5 text-xs">
                          <User className="size-3 text-muted-foreground" />
                          <span>{acc.owner.first_name} {acc.owner.last_name || ""}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {acc.created_on ? new Date(acc.created_on).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(acc)} className="size-8">
                        <Edit className="size-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteAccount(acc)} className="size-8 text-destructive">
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={addOpen || !!editAccount} onOpenChange={(open) => !open && (setAddOpen(false), setEditAccount(null))}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={editAccount ? handleEditSubmit : handleAddSubmit}>
            <DialogHeader>
              <DialogTitle>{editAccount ? "Edit Account" : "Add Account"}</DialogTitle>
              <DialogDescription>
                Fill in the organization contact and address specifications.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-4.5 py-4">
              <div className="space-y-2">
                <Label htmlFor="acc_name">Company / Organization Name *</Label>
                <Input
                  id="acc_name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_industry">Buyer Type</Label>
                  <select
                    id="acc_industry"
                    className="w-full h-10 sm:h-11 rounded-lg border border-input bg-background px-3 sm:px-3.5 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring"
                    value={form.industry}
                    onChange={(e) => setForm((p) => ({ ...p, industry: e.target.value }))}
                  >
                    <option value="">-- Select Buyer Type --</option>
                    {industries.map((ind) => (
                      <option key={ind.code} value={ind.name}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_website">Website</Label>
                  <Input
                    id="acc_website"
                    placeholder="e.g. company.com"
                    value={form.website}
                    onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_email">Corporate Email</Label>
                  <Input
                    id="acc_email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_phone">Corporate Phone</Label>
                  <Input
                    id="acc_phone"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="acc_billing">Billing Address</Label>
                <Input
                  id="acc_billing"
                  value={form.billing_address}
                  onChange={(e) => setForm((p) => ({ ...p, billing_address: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="acc_gstin">GSTIN</Label>
                  <Input
                    id="acc_gstin"
                    placeholder="27AAACN1234F1Z5"
                    maxLength={15}
                    value={form.gstin}
                    onChange={(e) => {
                      const gstin = e.target.value.toUpperCase();
                      // GSTIN starts with the state code, so fill the state when it is empty
                      setForm((p) => ({ ...p, gstin, state: p.state || stateFromGstin(gstin) }));
                    }}
                    className="font-mono uppercase"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acc_state">State</Label>
                  <select
                    id="acc_state"
                    value={form.state}
                    onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                    className="h-10 sm:h-11 w-full rounded-lg border border-border bg-background px-3 text-sm sm:text-[15px] text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
                  >
                    <option value="">-- Select state --</option>
                    {INDIAN_STATES.map((st) => (
                      <option key={st.code} value={st.name}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">Decides CGST + SGST or IGST on quotes</p>
                </div>
              </div>

              <BuyerTermsFields value={terms} onChange={setTerms} idPrefix="acc" />

              <div className="space-y-2">
                <Label htmlFor="acc_notes">Internal Notes</Label>
                <Input
                  id="acc_notes"
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => (setAddOpen(false), setEditAccount(null))}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : editAccount ? "Save Changes" : "Create Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteAccount} onOpenChange={(open) => !open && setDeleteAccount(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle>Confirm Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteAccount?.name}</strong>? All associated deals and contacts will be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAccount(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
